/**
 * Location as external state.
 *
 * Authority: bead scope-njw.
 *
 * `useSyncExternalStore` rather than `useState` + an effect: the URL is state
 * that lives outside React and changes without asking, and an effect-based
 * mirror of it renders one frame of the previous route before it catches up.
 * On a role-gated tree that frame is the previous role's screen.
 */
import { useSyncExternalStore } from 'react'

import { normalisePath } from '../routes/_registry/match.ts'

const listeners = new Set<() => void>()

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  window.addEventListener('popstate', listener)

  return () => {
    listeners.delete(listener)
    window.removeEventListener('popstate', listener)
  }
}

function currentPath(): string {
  return normalisePath(window.location.pathname)
}

/** The server renders nothing; this exists so the hook is legal in any host. */
function serverPath(): string {
  return '/'
}

export function useLocationPath(): string {
  return useSyncExternalStore(subscribe, currentPath, serverPath)
}

/**
 * Same-origin navigation. `pushState` does not fire `popstate`, so subscribers
 * are told directly — the alternative is a route change the router never sees.
 */
export function navigate(to: string): void {
  if (normalisePath(to) === currentPath()) return

  window.history.pushState(null, '', to)
  for (const listener of listeners) listener()
}
